import CodeBlock from "@tiptap/extension-code-block";
import { Document } from "@tiptap/extension-document";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import Link from "@tiptap/extension-link";
import Strike from "@tiptap/extension-strike";
import { Editor, useEditor as useTiptapEditor } from "@tiptap/react";

import BlurDecorator from "@/view/TiptapArticleBody/extensions/BlurDecorator";
import { ChecksForUnderstanding } from "@/view/TiptapArticleBody/extensions/ChecksForUnderstanding";
import ContentDrawerGroup from "@/view/TiptapArticleBody/extensions/ContentDrawerGroup/ContentDrawerGroup";
import { Core } from "@/view/TiptapArticleBody/extensions/core";
import { DataProviderExtension } from "@/view/TiptapArticleBody/extensions/DataProvider";
import { HardBreakBlock } from "@/view/TiptapArticleBody/extensions/HardBreakBlock";
import { HeadingBlock } from "@/view/TiptapArticleBody/extensions/HeadingBlock";
import IgnoreBlock from "@/view/TiptapArticleBody/extensions/IgnoreBlock";
import { Image } from "@/view/TiptapArticleBody/extensions/Image";
import { KeyTerm } from "@/view/TiptapArticleBody/extensions/KeyTerm";
import { Language } from "@/view/TiptapArticleBody/extensions/Language";
import {
  OrderedList,
  OrderedListItem,
  UnorderedList,
  UnorderedListItem,
} from "@/view/TiptapArticleBody/extensions/List";
import { ParagraphBlock } from "@/view/TiptapArticleBody/extensions/ParagraphBlock";
import { PowerWord } from "@/view/TiptapArticleBody/extensions/PowerWord";
import { PreformattedBlock } from "@/view/TiptapArticleBody/extensions/PreformattedBlock";
import QuoteBlock from "@/view/TiptapArticleBody/extensions/QuoteBlock";
import {
  Annotations,
  GuidedHighlighting,
} from "@/view/TiptapArticleBody/extensions/TextHighlighting";
import { INode } from "@/view/TiptapArticleBody/TiptapArticleBody.interface";

export interface IUseEditorProps {
  isPreview: boolean;
  focusAnnotationCard: (annotationId: any) => void;
  keyTermDataContentId?: string | undefined;
  levelId: string;
  rawText: INode | null;
  toggleVocabularySupport?: ((value: boolean) => void) | undefined;
}

const useEditor = ({
  isPreview: isArticlePreview,
  focusAnnotationCard,
  keyTermDataContentId,
  levelId,
  rawText,
  toggleVocabularySupport,
}: IUseEditorProps): Editor | null => {
  const config = {
    editorProps: {
      attributes: {
        class: "TipTapArticle-text",
        "data-testid": "TipTapArticle-Editor",
        translate: "no",
      },
    },
    extensions: [
      /*
      Make sure not to pass any dynamic data to .configure();
      Follow dynamicDataDependencies example to add a new data dependency.
      */
      DataProviderExtension,
      BlurDecorator,
      Core,
      ContentDrawerGroup,
      Document,
      HeadingBlock.configure({
        disableTranslation: isArticlePreview,
      }),
      Link,
      Image,
      Language,
      ...(isArticlePreview
        ? []
        : [
            Annotations.configure({
              focusAnnotationCard,
            }),
            GuidedHighlighting,
            ChecksForUnderstanding,
          ]),
      PowerWord.configure({
        isArticlePreview,
      }),
      KeyTerm.configure({
        isArticlePreview,
        toggleVocabularySupport,
        keyTermDataContentId, // keyTermDataContentId is one-to-one with article level. It won't cause editor state reset within a level.
      }),
      ParagraphBlock.configure({
        useLegacyHighlighting: true,
        disableTranslation: isArticlePreview,
      }),
      HardBreakBlock,
      PreformattedBlock.configure({
        disableTranslation: isArticlePreview,
      }),
      QuoteBlock.configure({
        disableTranslation: isArticlePreview,
      }),
      IgnoreBlock,
      OrderedList.configure({
        disableTranslation: isArticlePreview,
      }),
      OrderedListItem,
      UnorderedList.configure({
        disableTranslation: isArticlePreview,
      }),
      UnorderedListItem,
      HorizontalRule,
      Strike,
      CodeBlock,
    ],
    content: rawText,
    editable: false,
  };

  return useTiptapEditor(
    config,
    /*
    When these dependencies change, it is acceptable and
    in some cases desirable to reset editor state.
    */
    [levelId, rawText],
  );
};

export default useEditor;
